import { useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

export const CORES_DE_SALA = ["#2457FF", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#8B5CF6", "#64748B"];
export type SalaDaLista = { id: string; nome: string; cor: string | null; ordem: number; ativa: boolean; usada: boolean };

const campo = "h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm dark:border-border dark:bg-card";
const icone = "flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 disabled:opacity-30 dark:hover:bg-muted";

function Cor({ valor, onChange }: { valor: string | null; onChange: (c: string) => void }) {
  return (
    <div className="flex gap-1">
      {CORES_DE_SALA.map((c) => (
        <button key={c} type="button" aria-label={`Cor ${c}`} aria-pressed={valor === c} onClick={() => onChange(c)} className={cn("h-5 w-5 rounded-full ring-offset-2 dark:ring-offset-card", valor === c && "ring-2 ring-zinc-400")} style={{ backgroundColor: c }} />
      ))}
    </div>
  );
}

/** O cadastro das salas: nome, cor, ordem e ativa. Usada não se exclui — desativa. `compacta`
 *  (diálogo de /salas no celular): as cores descem pra segunda linha, pro nome caber. */
export function ListaDeSalas({ salas, compacta = false }: { salas: SalaDaLista[]; compacta?: boolean }) {
  const utils = trpc.useUtils();
  const [nome, setNome] = useState("");
  const [cor, setCor] = useState(CORES_DE_SALA[0]!);
  const depois = (msg?: string) => () => {
    if (msg) toast.success(msg);
    utils.salas.invalidate();
    utils.appointment.invalidate();
    // A planta lida esconde sala desativada: sem isso o editor salvaria a sala que acabou de sair.
    utils.planta.invalidate();
  };
  const erro = (e: { message: string }) => toast.error(e.message);
  const criar = trpc.salas.criar.useMutation({ onSuccess: () => (setNome(""), depois("Sala criada.")()), onError: erro });
  const editar = trpc.salas.editar.useMutation({ onSuccess: depois(), onError: erro });
  const desativar = trpc.salas.desativar.useMutation({ onSuccess: depois("Sala desativada."), onError: erro });
  const reativar = trpc.salas.reativar.useMutation({ onSuccess: depois("Sala reativada."), onError: erro });
  const excluir = trpc.salas.excluir.useMutation({ onSuccess: depois("Sala excluída."), onError: erro });

  const ativas = salas.filter((s) => s.ativa);
  const inativas = salas.filter((s) => !s.ativa);
  // Trocar a ordem com a vizinha: as duas gravam a posição da outra.
  const mover = (i: number, passo: -1 | 1) => {
    const a = ativas[i]!;
    const b = ativas[i + passo]!;
    editar.mutate({ id: a.id, ordem: b.ordem === a.ordem ? a.ordem + passo : b.ordem });
    editar.mutate({ id: b.id, ordem: a.ordem });
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => (e.preventDefault(), nome.trim() && criar.mutate({ nome: nome.trim(), cor }))}
        className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-zinc-300 p-3 dark:border-border"
      >
        <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Consultório 1, Cadeira A, Sala de cirurgia" maxLength={40} className={cn(campo, "min-w-0 flex-1", compacta && "basis-full")} />
        <Cor valor={cor} onChange={setCor} />
        <button type="submit" disabled={!nome.trim() || criar.isPending} className={cn("inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50", compacta && "ml-auto")}>
          {criar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Nova sala
        </button>
      </form>

      {ativas.length > 0 && (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border/50 dark:border-border">
          {ativas.map((s, i) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: s.cor ?? "#94a3b8" }} />
              <input
                defaultValue={s.nome}
                aria-label={`Nome da ${s.nome}`}
                maxLength={40}
                onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== s.nome && editar.mutate({ id: s.id, nome: e.target.value.trim() })}
                className={cn(campo, "h-8 min-w-0 flex-1 border-transparent hover:border-zinc-200")}
              />
              <div className={cn(compacta && "order-last basis-full pl-6")}>
                <Cor valor={s.cor} onChange={(c) => editar.mutate({ id: s.id, cor: c })} />
              </div>
              <div className="flex">
                <button type="button" aria-label="Subir" disabled={i === 0} onClick={() => mover(i, -1)} className={icone}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" aria-label="Descer" disabled={i === ativas.length - 1} onClick={() => mover(i, 1)} className={icone}><ArrowDown className="h-4 w-4" /></button>
              </div>
              <button type="button" onClick={() => desativar.mutate({ id: s.id })} className="text-[12px] font-medium text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground">
                Desativar
              </button>
            </li>
          ))}
        </ul>
      )}

      {inativas.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Desativadas</p>
          <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border/50 dark:border-border">
            {inativas.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm text-zinc-500">
                <span className="h-3 w-3 shrink-0 rounded-full opacity-50" style={{ backgroundColor: s.cor ?? "#94a3b8" }} />
                <span className="min-w-0 flex-1 truncate">{s.nome}</span>
                <button type="button" onClick={() => reativar.mutate({ id: s.id })} className="inline-flex items-center gap-1 text-[12px] font-medium hover:text-zinc-800">
                  <RotateCcw className="h-3.5 w-3.5" /> Reativar
                </button>
                {!s.usada && (
                  <button type="button" onClick={() => window.confirm(`Excluir a ${s.nome}?`) && excluir.mutate({ id: s.id })} className="inline-flex items-center gap-1 text-[12px] font-medium text-red-600 hover:text-red-700">
                    <Trash2 className="h-3.5 w-3.5" /> Excluir
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
